import { t, useLocale } from "@/i18n/locale";
import LoadingView from "@/components/LoadingView";
import { Message } from "@/i18n/Message";
import { lazy, useEffect } from "react";
import {
  detailsNavigationTarget,
  legacyInstanceDetailTarget,
} from "@/components/personal-apps/discovery/discoveryUrl";
import DiscoveryWorkspace from "@/components/personal-apps/discovery/DiscoveryWorkspace";
import type { DiscoveryUrlState } from "@/components/personal-apps/discovery/types";
import { isDiscoveryWorkspaceEnabled } from "@/config/featureFlags";
// Account-mode pages (api.ato.run cookie session only). Never gated on a
// Desktop Runtime; never call /v1/runtime/*.
import { initiateSignIn } from "../../auth/accountCallback";
import { resolveSettingsSection } from "../../components/settingsSections";
import { isTransfersEnabled } from "../../config/featureFlags";
import { isDesktop } from "../../desktop/bridge";
import { isRouteAvailable } from "../../config/featureFlags";
import { hasCachedMyApps } from "@/components/personal-apps/myAppsStore";
import {
  homeViewTarget,
  type HomeView,
} from "@/components/personal-apps/homeView";
import {
  parseInviteRoute,
  parsePersonalAppsRoute,
} from "../routing/routeParser";
import type { NavigateOptions } from "../routing/useAppNavigation";
import type { AccountController } from "../account/useAccountController";
import { runsClient, runnersClient } from "../apiClients";

const DeveloperProfilePage = lazy(
  () => import("@/components/personal-apps/discovery/DeveloperProfilePage"),
);
const LaunchReturnRoute = lazy(() => import("@/components/LaunchReturnRoute"));
const SettingsPlans = lazy(() => import("../../components/SettingsPlans"));
const FormationInputPage = lazy(
  () => import("../../components/FormationInputPage"),
);
const SettingsBillingReturn = lazy(
  () => import("../../components/SettingsBillingReturn"),
);
const DesktopLanding = lazy(() => import("../../components/DesktopLanding"));
const InstanceLaunch = lazy(
  () => import("@/components/personal-apps/InstanceLaunch"),
);
const McpPermissionsPage = lazy(
  () => import("../../components/McpPermissionsPage"),
);
const SettingsIndex = lazy(() => import("../../components/SettingsIndex"));
const SettingsSectionPage = lazy(
  () => import("../../components/SettingsSectionPage"),
);
const RunnersList = lazy(() => import("../../components/RunnersList"));
const HomeSplitView = lazy(
  () => import("@/components/personal-apps/HomeSplitView"),
);
const CreatorPage = lazy(
  () => import("@/components/personal-apps/CreatorPage"),
);
const CreateAppPage = lazy(
  () => import("@/components/personal-apps/CreateAppPage"),
);
const AppReadyRoute = lazy(
  () => import("@/components/personal-apps/AppReadyRoute"),
);
const InviteLandingPage = lazy(
  () => import("@/components/personal-apps/share/InviteLandingPage"),
);
const SignedOutLanding = lazy(
  () => import("../../components/landing/SignedOutLanding"),
);

function LegacyAppDetailRedirect({
  target,
  navigate,
}: {
  target: string;
  navigate: (route: string, opts?: NavigateOptions) => void;
}) {
  useEffect(() => {
    navigate(target, { replace: true });
  }, [target, navigate]);
  // The redirect is immediate; this is the neutral blank already used while a
  // home view resolves, not a second "not found" state.
  return <div className="landing-resolve" aria-hidden="true" />;
}

export default function AccountRoutes({
  route,
  navigate,
  addSource = null,
  homeView = "split",
  discoveryState = null,
  account,
}: {
  route: string;
  navigate: (route: string, opts?: NavigateOptions) => void;
  addSource?: string | null;
  /**
   * The Home split-view arrangement from the URL (`?view=…`). Optional so
   * older call sites keep rendering the default split; App.tsx always passes
   * the live value from `useAppNavigation`.
   */
  homeView?: HomeView;
  discoveryState?: DiscoveryUrlState | null;
  account: AccountController;
}) {
  useLocale();
  const {
    accountState,
    setAccountState,
    commitSignedInSession,
    refreshAccountState,
    handleAccountSignIn,
    handleAccountSignOut,
  } = account;
  if (route === "/creator" || route === "/studio") {
    if (accountState.status === "loading") return <LoadingView />;
    if (accountState.status !== "signed_in")
      return (
        <section className="studio-signedout">
          <h1>{t("Studio")}</h1>
          <p>{t("Sign in to create, publish, and analyze your apps.")}</p>
          <div className="studio-signedout-actions">
            <button
              type="button"
              className="wizard-btn wizard-btn--primary"
              onClick={handleAccountSignIn}
            >
              {t("Sign in")}
            </button>
            <button
              type="button"
              className="wizard-btn wizard-btn--ghost"
              onClick={() => navigate("/?view=discover")}
            >
              {t("Explore apps")}
            </button>
          </div>
        </section>
      );
    return (
      <CreatorPage key={accountState.session.user.id} onNavigate={navigate} />
    );
  }
  const formationInput = /^\/formations\/([A-Za-z0-9_-]{1,256})\/input$/.exec(
    route,
  );
  const developerProfile = /^\/developers\/([^/]+)$/.exec(route);
  if (developerProfile)
    return (
      <DeveloperProfilePage
        id={decodeURIComponent(developerProfile[1])}
        accountState={accountState}
        onNavigate={navigate}
      />
    );
  if (route === "/launch/return") {
    return (
      <LaunchReturnRoute
        accountState={accountState}
        navigate={navigate}
        onSignIn={handleAccountSignIn}
      />
    );
  }
  if (formationInput) {
    if (accountState.status === "loading")
      return <div className="landing-resolve" aria-hidden="true" />;
    if (accountState.status !== "signed_in")
      return (
        <button className="settings-button" onClick={handleAccountSignIn}>
          <Message message={"Sign in to provide Formation input"} />
        </button>
      );
    return <FormationInputPage searchId={formationInput[1]} />;
  }
  // Ato Desktop marketing landing. Rendered inside the same shell as the rest
  // of the app (gallery variant) rather than as a standalone full-page route.
  if (route === "/desktop") {
    return <DesktopLanding />;
  }
  // An invite link, from anybody, to anybody. Checked BEFORE the Personal
  // Apps flag: the person following it is being shown somebody else's App, and
  // whether the flag happens to be on for them is not their problem.
  const inviteRoute = parseInviteRoute(route);
  if (inviteRoute) {
    return (
      <InviteLandingPage
        key={inviteRoute.token}
        token={inviteRoute.token}
        signedIn={accountState.status === "signed_in"}
        onSignIn={handleAccountSignIn}
        onNavigate={navigate}
      />
    );
  }

  // Personal Apps. `/` IS My Apps: an App whose whole promise is "it is
  // there when you come back" cannot live one click down.
  //
  // These pages do NOT run through any PWA session/console controller: an
  // App's lifetime is owned by its Instance URL, not by PWA navigation. Closing
  // an embedded view releases the view only; nothing about the App stops.
  const personalAppsRoute = parsePersonalAppsRoute(route);
  if (personalAppsRoute) {
    // Home is one screen: My Apps and Discover side by side.
    if (personalAppsRoute.kind === "list") {
      if (
        (isDiscoveryWorkspaceEnabled() || discoveryState?.selection) &&
        homeView !== "my-apps"
      )
        return (
          <DiscoveryWorkspace
            accountState={accountState}
            onAccountRefresh={refreshAccountState}
            urlState={
              discoveryState ?? { selection: null, themeId: null, query: "" }
            }
            userId={
              accountState.status === "signed_in"
                ? accountState.session.user.id
                : null
            }
            onSignIn={handleAccountSignIn}
            onNavigate={navigate}
          />
        );
      // Blank only while there is genuinely nothing to show. With a cached
      // launcher snapshot, `/` paints it and the session check resolves
      // behind it — My Apps is a home screen, and a home screen that waits
      // on a round trip before drawing anything is the defect this avoids.
      // `authState` below carries the distinction, so nothing here claims
      // the person is signed in before the check answers.
      if (accountState.status === "loading" && !hasCachedMyApps()) {
        return <div className="landing-resolve" aria-hidden="true" />;
      }
      if (accountState.status === "signed_out") {
        return (
          <div>
            <SignedOutLanding
              onSignIn={handleAccountSignIn}
              onNavigate={navigate}
            />
          </div>
        );
      }
      // A session check that FAILED is not a signed-out person: offline, or
      // an API that blinked. With a cached launcher there is something
      // honest to show, so show it (read-only — `authState` stays
      // unconfirmed) instead of replacing everybody's apps with an error.
      if (accountState.status === "error" && !hasCachedMyApps()) {
        return (
          <div className="home-screen">
            <div className="wizard-alert wizard-alert--danger" role="alert">
              <Message message={"Couldn’t check whether you’re signed in."} />
            </div>
            <button
              type="button"
              className="wizard-btn"
              onClick={() => void refreshAccountState()}
            >
              <Message message={"Try again"} />
            </button>
          </div>
        );
      }
      return (
        <HomeSplitView
          view={homeView}
          signedIn={accountState.status === "signed_in"}
          // `error` is deliberately NOT `signed_out`: signing out erases the
          // cached launcher, and a failed session check must never do that.
          // (An actual `signed_out` returned the landing page above, so only
          // verified / still-checking reach this screen.)
          authState={
            accountState.status === "signed_in" ? "verified" : "pending"
          }
          userId={
            accountState.status === "signed_in"
              ? accountState.session.user.id
              : ""
          }
          onSignIn={handleAccountSignIn}
          onNavigate={navigate}
          onAdd={() => navigate("/apps/new")}
          onOpenDetail={(instanceId) =>
            navigate(
              detailsNavigationTarget(
                { kind: "instance", id: instanceId },
                "overview",
                window.location.pathname + window.location.search,
              ),
            )
          }
          // `replace`: flipping panes is a view toggle, not a place — it
          // must not stack a history entry per click, while the URL still
          // reflects the view so reloads restore it.
          onViewChange={(view) =>
            navigate(homeViewTarget(view), { replace: true })
          }
        />
      );
    }
    if (personalAppsRoute.kind === "new") {
      // Do not render the Add App form before the session resolves: its key
      // changes from "signed_out" to the account id, remounting the form and
      // aborting an upload that started in that window.
      if (accountState.status === "loading") return <LoadingView />;
      if (accountState.status !== "signed_in")
        return (
          <button className="settings-button" onClick={handleAccountSignIn}>
            <Message message={"Sign in"} />
          </button>
        );
      return (
        <CreateAppPage
          key={accountState.session.user.id}
          userId={accountState.session.user.id}
          initialSource={addSource}
          // Straight to the link, not to a settings page: the first thing a
          // person should learn about their new App is that it has its own
          // address and Ato is not in the way of it.
          onAdded={(app) =>
            navigate(
              `/apps/${app.instance_id}/ready${new URLSearchParams(window.location.search).get("intent") === "publish" ? "?intent=publish" : ""}`,
            )
          }
          onCancel={() =>
            navigate(
              new URLSearchParams(window.location.search).get("from") ===
                "studio"
                ? "/studio"
                : "/",
            )
          }
        />
      );
    }
    if (personalAppsRoute.kind === "detail") {
      // /apps/<id> used to be a page. The product now shows the app as a
      // detail modal on the home surface; redirecting keeps old links, shared
      // URLs and bookmarks working instead of falling through to "Page not
      // found".
      return (
        <LegacyAppDetailRedirect
          target={legacyInstanceDetailTarget(personalAppsRoute.instanceId)}
          navigate={navigate}
        />
      );
    }
    if (personalAppsRoute.kind === "open") {
      return (
        <InstanceLaunch
          instanceId={personalAppsRoute.instanceId}
          onBack={() => navigate("/")}
        />
      );
    }
    if (personalAppsRoute.kind === "ready") {
      return (
        <AppReadyRoute
          key={personalAppsRoute.instanceId}
          instanceId={personalAppsRoute.instanceId}
          publishing={
            new URLSearchParams(window.location.search).get("intent") ===
            "publish"
          }
          onDone={() => navigate("/")}
          onPublish={() =>
            navigate(
              detailsNavigationTarget(
                { kind: "instance", id: personalAppsRoute.instanceId },
                "overview",
                "/?view=my-apps",
              ) + "&publish=1",
            )
          }
        />
      );
    }
    // The list (`/`) is the one screen whose content the account state
    // chooses — signed_out gets the Product Landing Page, loading a neutral
    // blank under the header, error an explicit retry. All three are handled
    // in the list branch above.
  }
  // Runners is a desktop-only route — gated by the shared isRouteAvailable
  // helper that also hides it from the nav.
  if (route === "/runners") {
    if (!isRouteAvailable("/runners", { desktop: isDesktop() })) {
      return (
        <div className="empty-state">
          <Message message={"This page is only available in Ato Desktop."} />
        </div>
      );
    }
    return (
      <RunnersList
        client={runnersClient}
        accountState={accountState}
        onAccountSignIn={handleAccountSignIn}
      />
    );
  }
  if (route === "/settings") {
    return (
      <SettingsIndex
        accountState={accountState}
        onAccountSignIn={initiateSignIn}
        onAccountSignOut={handleAccountSignOut}
        onAccountSignedIn={commitSignedInSession}
        onOpenSection={(section) => navigate(`/settings/${section}`)}
      />
    );
  }
  if (
    route === "/settings/permissions" ||
    /^\/settings\/permissions\/[^/]+$/.test(route)
  ) {
    const id = route.slice("/settings/permissions/".length);
    return (
      <McpPermissionsPage
        key={route}
        instanceId={id ? decodeURIComponent(id) : undefined}
        navigate={navigate}
      />
    );
  }
  if (route === "/settings/billing/return") {
    return <SettingsBillingReturn navigate={navigate} />;
  }
  if (route === "/settings/billing/plans")
    return <SettingsPlans navigate={navigate} />;
  if (route.startsWith("/settings/")) {
    // `resolveSettingsSection` also follows legacy aliases (`/settings/account`
    // → Sign-in & security), so an old bookmark still lands on the page that
    // now owns that content instead of falling through to "Unknown route".
    const section = resolveSettingsSection(route.slice("/settings/".length));
    if (section && (section !== "transfer" || isTransfersEnabled())) {
      return (
        <SettingsSectionPage
          key={section}
          section={section}
          navigate={navigate}
          accountState={accountState}
          loadCloudUsage={() => runsClient.getCloudUsage()}
          onNameSaved={() => void refreshAccountState()}
          onSignOut={handleAccountSignOut}
          onDeleted={() => {
            setAccountState({ status: "signed_out" });
            navigate("/settings");
          }}
          onBack={() => navigate("/settings")}
        />
      );
    }
  }
  // A genuinely unrecognized route — an honest fallback rather than silently
  // rendering the Showcase feed, which would hide a broken/typo'd deep link
  // behind working-looking content.
  return (
    <div className="empty-state">
      <h1>
        <Message message="Page not found" />
      </h1>
      <p>
        <Message message="This page may have moved or been removed." />
      </p>
      <button type="button" className="btn" onClick={() => navigate("/")}>
        <Message message="Explore apps" />
      </button>
    </div>
  );
}
